import React from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { ChakraProvider } from "@chakra-ui/react";
import { init, ErrorBoundary, showReportDialog } from "@sentry/react";
import { ToasterProvider } from "@chainsafe/common-components";
import Routes from "./Components/Routes";
import { ChainbridgeProvider } from "./Contexts/ChainbridgeContext";
import AppWrapper from "./Layouts/AppWrapper";
import { Web3Provider } from "@chainsafe/web3-context";
import { chainbridgeConfig, networkIds } from "./chainbridgeConfig";
import { utils } from "ethers";
import Fonts from "./Components/Fonts";
import theme from "./Themes/theme";

if (
  process.env.NODE_ENV === "production" &&
  process.env.REACT_APP_SENTRY_DSN_URL &&
  process.env.REACT_APP_SENTRY_RELEASE
) {
  init({
    dsn: process.env.REACT_APP_SENTRY_DSN_URL,
    release: process.env.REACT_APP_SENTRY_RELEASE,
  });
}

const App: React.FC<{}> = () => {
  const tokens = chainbridgeConfig.chains.reduce((tca, bc) => {
    return {
      ...tca,
      [bc.networkId]: bc.tokens,
    };
  }, {});
  return (
    <ErrorBoundary
      fallback={({ error, componentStack, eventId, resetError }) => (
        <div>
          <p>
            An error occurred and has been logged. If you would like to provide
            additional info to help us debug and resolve the issue, click the
            &quot;Provide Additional Details&quot; button
          </p>
          <p>{error?.message.toString()}</p>
          <p>{componentStack}</p>
          <p>{eventId}</p>
          <button onClick={() => showReportDialog({ eventId: eventId || "" })}>
            Provide Additional Details
          </button>
          <button onClick={resetError}>Reset error</button>
        </div>
      )}
      onReset={() => window.location.reload()}
    >
      <ChakraProvider theme={theme}>
        <Fonts />
        <ToasterProvider autoDismiss>
          <Web3Provider
            tokensToWatch={tokens}
            onboardConfig={{
              dappId: process.env.REACT_APP_BLOCKNATIVE_DAPP_ID,
              networkName: chainbridgeConfig.chains[1].name,
              walletSelect: {
                wallets: [
                  { walletName: "metamask", preferred: true },
                  { walletName: "alphawallet", preferred: true },
                ],
              },
              subscriptions: {
                address: (address) => console.log("address: ", address),
                balance: (amount) =>
                  amount && console.log("balance: ", utils.formatEther(amount)),
                network: (network) => console.log("networkId: ", network),
                wallet: (wallet) => console.log("wallet: ", wallet.name),
              },
            }}
            checkNetwork={false}
            // gasPricePollingInterval={120}
            networkIds={networkIds()}
            // gasPriceSetting="fast"
          >
            <ChainbridgeProvider>
              <Router>
                <AppWrapper>
                  <Routes />
                </AppWrapper>
              </Router>
            </ChainbridgeProvider>
          </Web3Provider>
        </ToasterProvider>
      </ChakraProvider>
    </ErrorBoundary>
  );
};

export default App;

// the following code is to clean-up the onboard app -- which currently does a nasty hack responding to both network
// changed and chain changed events.  Only the chain changed event should be used, especially because it's the chain id
// that is used, not the network id. So we agressively remove all networkChanged events from the ethereum event emitter
// object
declare const window: any;
if (window.ethereum) {
  window.ethereum.removeAllListeners("networkChanged");
  window.ethereum.on("chainChanged", () => {
    window.ethereum.removeAllListeners("networkChanged");
  });
}
